import {Inject, Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {BehaviorSubject, Observable, tap, firstValueFrom} from 'rxjs';
import {User} from '@models/user.interface';
import {Auth} from 'firebase/auth';
import {AUTH} from '@core/firebase/firebase-auth.token';
import {AuthUtilsService} from '@core/auth/guards/auth-utils.service';
import {firebaseAuthApi} from '@core/firebase/firebase-auth.api';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    public readonly user$: Observable<User | null>;
    private readonly _user: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);

    constructor(
        private readonly _httpClient: HttpClient,
        @Inject(AUTH) private readonly _auth: Auth,
        private readonly _authUtilsService: AuthUtilsService
    ) {
        this.user$ = this._user.asObservable();
    }

    set user(value: User | null) {
        this._user.next(value);
        if (value) localStorage.setItem('user', JSON.stringify(value));
        else localStorage.removeItem('user');
    }

    async initializeUser(): Promise<void> {

        try {
            const cachedUser = localStorage.getItem('user');
            if (cachedUser) {
                this._user.next(JSON.parse(cachedUser));
                return;
            }

            const firebaseUser = await firstValueFrom(firebaseAuthApi.authState(this._auth));
            if (firebaseUser) {
                const user = await this._authUtilsService.fetchUserFromServer(firebaseUser);
                this._user.next(user);
                localStorage.setItem('user', JSON.stringify(user));
            } else {
                this._user.next(null);
                localStorage.removeItem('user');
            }
        } catch (error) {
            console.error('Error initializing user:', error);
            this._user.next(null);
            localStorage.removeItem('user');
        }
    }

    get(): Observable<User | null> {
        return this._httpClient.get<User>('api/common/user').pipe(
            tap((user) => {
                this._user.next(user);
                if (user) localStorage.setItem('user', JSON.stringify(user));
            })
        );
    }

    update(user: User): Observable<any> {
        return this._httpClient.patch<User>('api/common/user', {user}).pipe(
            tap((response) => {
                this._user.next(response);
                if (response) localStorage.setItem('user', JSON.stringify(response));
            })
        );
    }
}
